import React, { useEffect, useMemo, useState } from "react";
import { api, exportCsv, fmtQty } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download } from "lucide-react";
import { DataTable } from "@/components/common/DataTable";
import { Dot, KindBadge, ReadyBadge, ShipStatus, ItemCell, d10 } from "@/components/common/cells";
import { POStatus } from "@/components/purchasing/POStatus";

const tabCls = "data-[state=active]:bg-slate-900 data-[state=active]:text-white";
const yes = (ok) => (ok ? "OK" : "X");
const shortCls = (v) => `font-mono ${v > 0 ? "text-red-700 font-semibold" : "text-emerald-700"}`;

function ReportCard({ title, url, report, testid, buildColumns, rowKey }) {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(url).then((r) => setRows(r.data)); }, [url]);
  const columns = useMemo(() => buildColumns(t), [t, buildColumns]);
  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
          <Button size="sm" variant="outline" onClick={() => exportCsv(report)} data-testid={`${testid}-export-btn`}>
            <Download size={14} className="mr-2" />{t("export_excel")}
          </Button>
        </div>
        <DataTable columns={columns} rows={rows} rowKey={rowKey} testid={`${testid}-report-table`} />
      </CardContent>
    </Card>
  );
}

const priorityCols = (t) => [
  { key: "rank", label: "#", align: "center", className: "font-bold text-slate-500" },
  { key: "plan_code", label: t("plan_code"), className: "font-mono text-xs" },
  { key: "buyer", label: t("buyers") },
  { key: "destination_country", label: t("destination_country") },
  { key: "stuffing_date", label: t("stuffing_date"), className: "font-mono text-xs whitespace-nowrap", value: (r) => d10(r.stuffing_date) },
  { key: "etd", label: t("etd"), className: "font-mono text-xs whitespace-nowrap", value: (r) => d10(r.etd) },
  { key: "qty", label: t("qty"), align: "right", className: "font-mono", render: (r) => fmtQty(r.qty) },
  { key: "vessel", label: t("vessel") },
  { key: "fg_ok", label: "FG", align: "center", value: (r) => yes(r.fg_status.ok), render: (r) => <Dot ok={r.fg_status.ok} /> },
  { key: "mc_ok", label: "MC", align: "center", value: (r) => yes(r.carton_status.ok), render: (r) => <Dot ok={r.carton_status.ok} /> },
  { key: "pl_ok", label: "PL", align: "center", value: (r) => yes(r.plastic_status.ok), render: (r) => <Dot ok={r.plastic_status.ok} /> },
  { key: "booking_ok", label: "Booking", align: "center", value: (r) => yes(r.booking_ok), render: (r) => <Dot ok={r.booking_ok} /> },
  { key: "ready", label: t("status"), align: "center", value: (r) => (r.ready ? t("ready") : t("pending")), render: (r) => <ReadyBadge ready={r.ready} t={t} /> },
];

const readinessCols = (t) => [
  { key: "plan_code", label: t("plan_code"), className: "font-mono text-xs" },
  { key: "buyer", label: t("buyers") },
  { key: "qty", label: t("qty"), align: "right", className: "font-mono", render: (r) => fmtQty(r.qty) },
  { key: "fg_short", label: `FG ${t("shortage")}`, align: "right", value: (r) => r.fg_status.shortage, render: (r) => <span className={shortCls(r.fg_status.shortage)}>{fmtQty(r.fg_status.shortage)}</span> },
  { key: "mc_short", label: `MC ${t("shortage")}`, align: "right", value: (r) => r.carton_status.shortage, render: (r) => <span className={shortCls(r.carton_status.shortage)}>{fmtQty(r.carton_status.shortage)}</span> },
  { key: "pl_short", label: `PL ${t("shortage")}`, align: "right", value: (r) => r.plastic_status.shortage, render: (r) => <span className={shortCls(r.plastic_status.shortage)}>{fmtQty(r.plastic_status.shortage)}</span> },
  { key: "booking_ok", label: "Booking", align: "center", value: (r) => yes(r.booking_ok), render: (r) => <Dot ok={r.booking_ok} /> },
  { key: "status", label: t("shipments"), align: "center", render: (r) => <ShipStatus status={r.status} /> },
  { key: "ready", label: t("status"), align: "center", value: (r) => (r.ready ? t("ready") : t("pending")), render: (r) => <ReadyBadge ready={r.ready} t={t} /> },
];

const outstandingCols = (t) => [
  { key: "po_number", label: t("po_number"), className: "font-mono text-xs" },
  { key: "item_type", label: t("kind"), value: (r) => String(r.item_type).toUpperCase(), render: (r) => <KindBadge kind={r.item_type} /> },
  { key: "item", label: t("item"), value: (r) => `${r.item_code} ${r.item_name}`, render: (r) => <ItemCell code={r.item_code} name={r.item_name} /> },
  { key: "qty", label: t("qty"), align: "right", className: "font-mono", render: (r) => fmtQty(r.qty) },
  { key: "received_qty", label: t("received"), align: "right", className: "font-mono text-emerald-700", render: (r) => fmtQty(r.received_qty) },
  { key: "remaining_qty", label: t("remaining"), align: "right", className: "font-mono font-semibold text-amber-700", render: (r) => fmtQty(r.remaining_qty) },
  { key: "expected_date", label: t("expected_date"), className: "font-mono text-xs whitespace-nowrap", value: (r) => d10(r.expected_date) },
  { key: "vendor", label: t("vendor") },
  { key: "status", label: t("status"), align: "center", render: (r) => <POStatus status={r.status} /> },
];

const ledgerCols = (t) => [
  { key: "kind", label: t("kind"), value: (r) => String(r.kind).toUpperCase(), render: (r) => <KindBadge kind={r.kind} /> },
  { key: "code", label: t("code"), className: "font-mono text-xs" },
  { key: "name", label: t("name"), className: "font-medium" },
  { key: "initial_stock", label: t("initial_stock"), align: "right", className: "font-mono", render: (r) => fmtQty(r.initial_stock) },
  { key: "allocated", label: t("allocated"), align: "right", className: "font-mono", render: (r) => fmtQty(r.allocated) },
  { key: "available_after_alloc", label: t("after_alloc"), align: "right", render: (r) => <span className={`font-mono font-semibold ${r.available_after_alloc < 0 ? "text-red-700" : ""}`}>{fmtQty(r.available_after_alloc)}</span> },
  { key: "incoming_po", label: t("incoming"), align: "right", className: "font-mono text-sky-700", render: (r) => fmtQty(r.incoming_po) },
  { key: "forecast_balance", label: t("forecast_balance"), align: "right", render: (r) => <span className={`font-mono font-semibold ${r.forecast_balance < 0 ? "text-red-700" : "text-emerald-700"}`}>{fmtQty(r.forecast_balance)}</span> },
];

const withRank = (rows) => rows.map((r, i) => ({ ...r, rank: i + 1 }));

function PriorityReport() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/reports/shipment-priority").then((r) => setRows(withRank(r.data))); }, []);
  const columns = useMemo(() => priorityCols(t), [t]);
  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("priority_report")}</h3>
          <Button size="sm" variant="outline" onClick={() => exportCsv("priority")} data-testid="priority-export-btn"><Download size={14} className="mr-2" />{t("export_excel")}</Button>
        </div>
        <DataTable columns={columns} rows={rows} testid="priority-report-table" />
      </CardContent>
    </Card>
  );
}

export default function Reports() {
  const { t } = useLang();
  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("reports")}</h1>
        <p className="text-sm text-slate-500 mt-1">Priority · Readiness · Outstanding PO · Stock Ledger</p>
      </div>
      <Tabs defaultValue="priority">
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          <TabsTrigger value="priority" data-testid="tab-priority" className={tabCls}>{t("priority_report")}</TabsTrigger>
          <TabsTrigger value="readiness" data-testid="tab-readiness" className={tabCls}>{t("readiness_report")}</TabsTrigger>
          <TabsTrigger value="outstanding" data-testid="tab-outstanding" className={tabCls}>{t("outstanding_po")}</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger" className={tabCls}>{t("stock_ledger")}</TabsTrigger>
        </TabsList>
        <TabsContent value="priority" className="mt-4"><PriorityReport /></TabsContent>
        <TabsContent value="readiness" className="mt-4">
          <ReportCard title={t("readiness_report")} url="/reports/readiness" report="readiness" testid="readiness" buildColumns={readinessCols} />
        </TabsContent>
        <TabsContent value="outstanding" className="mt-4">
          <ReportCard title={t("outstanding_po")} url="/reports/outstanding-po" report="outstanding-po" testid="outstanding" buildColumns={outstandingCols} rowKey={(r) => `${r.id}-${r.line_id}`} />
        </TabsContent>
        <TabsContent value="ledger" className="mt-4">
          <ReportCard title={t("stock_ledger")} url="/reports/stock-ledger" report="stock-ledger" testid="ledger" buildColumns={ledgerCols} rowKey={(r) => `${r.kind}-${r.id}`} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
